import { mergeAttributes, Node } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { Check, Loader2, Sparkles, X } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { translateNaturalLanguageToLatexMutation } from '../../../db/api/translate-natural-language-to-latex.js';
import { cn } from '../../lib/utils.js';
import { useMathField } from './use-math-field.js';

async function translateNaturalLanguage(text: string, examId?: string): Promise<string> {
  return translateNaturalLanguageToLatexMutation(text, examId)();
}

function MathNodeView({ node, updateAttributes, deleteNode, selected, extension }: NodeViewProps) {
  const { t } = useTranslation();
  const examId = extension.options.examId as string | undefined;
  const latex = (node.attrs.latex as string) ?? '';
  const { containerRef } = useMathField({
    value: latex,
    onChange: value => updateAttributes({ latex: value }),
    className: 'inline-block min-w-[1.5rem] px-0.5 outline-none',
  });

  const [isNlOpen, setIsNlOpen] = React.useState(false);
  const [nlInput, setNlInput] = React.useState('');
  const [isNlLoading, setIsNlLoading] = React.useState(false);
  const [nlError, setNlError] = React.useState<string | null>(null);

  // Anchored (not absolutely-positioned-in-flow) so the panel can never end up clipped by an
  // ancestor's overflow:hidden/auto, or rendered off the edge of the scroll container — it's
  // portaled to <body> and flip/shift keep it inside the viewport regardless of where in the
  // text this formula sits.
  const { refs, floatingStyles, context } = useFloating({
    open: isNlOpen,
    onOpenChange: open => {
      if (open) setNlError(null);
      setIsNlOpen(open);
    },
    placement: 'top-start',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'dialog' });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  const submitNaturalLanguage = async () => {
    if (!nlInput.trim()) return;
    setIsNlLoading(true);
    setNlError(null);
    try {
      const translated = await translateNaturalLanguage(nlInput.trim(), examId);
      updateAttributes({ latex: translated });
      setNlInput('');
      setIsNlOpen(false);
    } catch {
      setNlError(t('LatexEditor.naturalLanguageError'));
    } finally {
      setIsNlLoading(false);
    }
  };

  return (
    <NodeViewWrapper
      as="span"
      className={cn(
        'group relative inline-flex items-baseline rounded border border-transparent px-0.5 align-middle',
        selected && 'border-brand-300 bg-brand-50 dark:border-brand-700 dark:bg-brand-900/20',
      )}
    >
      <span ref={containerRef} contentEditable={false} className="inline-block align-middle" />

      <span
        className={cn(
          'absolute -top-6 left-0 z-10 hidden items-center gap-0.5 rounded-md border border-slate-200 bg-white p-0.5 shadow-sm group-focus-within:flex group-hover:flex dark:border-slate-700 dark:bg-card',
          isNlOpen && 'flex',
        )}
      >
        <button
          ref={refs.setReference}
          {...getReferenceProps()}
          type="button"
          contentEditable={false}
          title={t('LatexEditor.naturalLanguageHint')}
          className="flex h-5 w-5 items-center justify-center rounded text-brand-600 hover:bg-brand-50 dark:text-brand-300 dark:hover:bg-brand-900/20"
        >
          <Sparkles size={11} />
        </button>
        <button
          type="button"
          contentEditable={false}
          onClick={() => deleteNode()}
          title={t('LatexEditor.removeFormula')}
          className="flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          <X size={11} />
        </button>
      </span>

      {isNlOpen && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
              className="z-50 flex w-72 flex-col gap-2 rounded-lg border border-slate-200 bg-white p-2 shadow-lg dark:border-slate-700 dark:bg-card"
            >
              <input
                autoFocus
                value={nlInput}
                onChange={e => setNlInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') void submitNaturalLanguage();
                }}
                placeholder={t('LatexEditor.naturalLanguagePlaceholder')}
                className="w-full rounded-md border border-input bg-transparent px-2 py-1.5 text-xs outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
              {nlError && <span className="text-xs text-red-600 dark:text-red-400">{nlError}</span>}
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsNlOpen(false)}
                  className="flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  <X size={13} /> {t('LatexEditor.cancel')}
                </button>
                <button
                  type="button"
                  onClick={() => void submitNaturalLanguage()}
                  disabled={isNlLoading || !nlInput.trim()}
                  className="flex items-center gap-1 rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-600 disabled:opacity-50"
                >
                  {isNlLoading ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                  {t('LatexEditor.insert')}
                </button>
              </div>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </NodeViewWrapper>
  );
}

export const MathInlineNode = Node.create({
  name: 'mathInline',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addOptions() {
    return {
      // Threaded down from LatexRichEditorProps so the natural-language-to-LaTeX call can attribute
      // its LLM cost to the exam it was made from (see translateNaturalLanguage above).
      examId: undefined as string | undefined,
    };
  },

  addAttributes() {
    return {
      latex: { default: '' },
      isBlock: { default: false },
    };
  },

  parseHTML() {
    return [{ tag: 'math-inline' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return ['math-inline', mergeAttributes(HTMLAttributes, { 'data-latex': node.attrs.latex as string })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(MathNodeView);
  },
});
